// src/components/InputStyled.jsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import { Check, ChevronDown, EllipsisVertical, FolderOpen } from "lucide-react";

/** ---------------- Browse input ---------------- */
export function BrowseInput({
  label,
  value,
  placeholder = "Select target",
  onBrowse,
  browseLabel = "Browse…",
  browseAriaLabel,
  loading = false,
  loadingText = "Loading targets…",
  disabled = false,
  className = "w-72",
  controlClassName = "",
}) {
  const displayValue = loading ? loadingText : value || "";

  return (
    <label className={`flex flex-col ${className}`}>
      {label ? (
        <span className="mb-1 text-xs font-medium text-muted">{label}</span>
      ) : null}
      <div className="flex min-w-0">
        <input
          type="text"
          value={displayValue}
          placeholder={placeholder}
          readOnly
          disabled={disabled}
          title={displayValue || placeholder}
          className={`h-8 min-w-0 flex-1 rounded-l-md border border-input bg-background px-3 text-sm text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-ring ${controlClassName} ${
            disabled ? "cursor-not-allowed opacity-70" : ""
          }`}
        />
        <button
          type="button"
          onClick={onBrowse}
          disabled={disabled || loading}
          aria-label={browseAriaLabel || browseLabel}
          className={`flex h-8 shrink-0 items-center gap-1.5 rounded-r-md border border-l-0 border-input bg-muted/20 px-2.5 text-xs font-medium text-foreground shadow-sm transition-colors hover:bg-muted/40 focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-70 ${controlClassName}`}
        >
          <FolderOpen size={14} />
          {browseLabel}
        </button>
      </div>
    </label>
  );
}

/** ---------------- File browse input ---------------- */
export function FileBrowseInput({
  label = "File",
  file,
  accept,
  placeholder = "Select file",
  browseLabel = "Browse…",
  browseAriaLabel = "Browse files",
  onFileChange,
  multiple = false,
  disabled = false,
  className = "w-96",
  controlClassName = "",
  inputKey,
}) {
  const fileInputRef = useRef(null);

  const browse = () => {
    if (disabled || !fileInputRef.current) return;
    fileInputRef.current.value = "";
    fileInputRef.current.click();
  };

  return (
    <>
      <input
        key={inputKey}
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        onChange={(event) => onFileChange?.(
          multiple ? Array.from(event.target.files || []) : event.target.files?.[0] || null,
          event
        )}
        className="hidden"
        tabIndex={-1}
      />
      <BrowseInput
        label={label}
        value={multiple
          ? (Array.isArray(file) && file.length
            ? `${file.length} file${file.length === 1 ? "" : "s"} selected`
            : "")
          : file?.name || ""}
        placeholder={placeholder}
        browseLabel={browseLabel}
        browseAriaLabel={browseAriaLabel}
        onBrowse={browse}
        disabled={disabled}
        className={className}
        controlClassName={controlClassName}
      />
    </>
  );
}

/** ---------------- Dropdown ---------------- */
export function DropdownSelect({
  label,
  value,
  onChange,
  options = [],
  placeholder = "Select…",
  className = "w-72",
  controlClassName = "",
  ariaLabel,
  disabled = false,
  name,
  required = false,
  autoComplete,
  mutedPlaceholder = false,
  ...rest
}) {
  return (
    <label className="flex flex-col">
      {label && (
        <span className="text-xs font-medium text-muted mb-1">{label}</span>
      )}

      <div className={`relative ${className}`}>
        <select
          aria-label={ariaLabel || label}
          disabled={disabled}
          name={name}
          required={required}
          autoComplete={autoComplete}
          className={`h-8 w-full appearance-none rounded-md border border-input bg-background pl-3 pr-9 text-sm shadow-sm
                      focus:outline-none focus:ring-2 focus:ring-ring
                      ${mutedPlaceholder && (value == null || value === "") ? "text-muted" : "text-foreground"}
                      ${disabled ? "cursor-not-allowed opacity-70" : ""} ${controlClassName}`}
          value={value ?? ""}
          onChange={(e) => {
            if (disabled) return;
            onChange?.(e.target.value, e);
          }}
          {...rest}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map((opt) => (
            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </option>
          ))}
        </select>
        <ChevronDown size={16} className={`pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-muted ${disabled ? "opacity-70" : ""}`} aria-hidden="true" />
      </div>
    </label>
  );
}

/** ---------------- Composite select ---------------- */
export function CompositeSelect({
  label,
  summary,
  children,
  className = "w-72",
  menuClassName = "w-80",
  ariaLabel,
  disabled = false,
  doneLabel = "Done",
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <div className={`relative ${className}`} ref={rootRef}>
      {label ? <span className="mb-1 block text-xs font-medium text-muted">{label}</span> : null}
      <button type="button" disabled={disabled} className={`flex h-8 w-full items-center justify-between gap-3 rounded-md border border-input bg-background px-3 text-left text-sm text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-ring ${disabled ? "cursor-not-allowed opacity-70" : ""}`} onClick={() => setOpen((current) => !current)} aria-haspopup="dialog" aria-expanded={open}>
        <span className="truncate">{summary}</span>
        <EllipsisVertical size={17} className="shrink-0 text-muted" aria-hidden="true" />
      </button>
      {open ? (
        <div role="dialog" aria-label={ariaLabel || `Configure ${label || "selection"}`} className={`absolute left-0 z-30 mt-2 max-w-[calc(100vw-2rem)] space-y-3 rounded-lg border border-border bg-app p-4 shadow-xl ${menuClassName}`}>
          {children}
          <div className="border-t border-border pt-3 text-right"><button type="button" className="btn btn-secondary text-sm" onClick={() => setOpen(false)}>{doneLabel}</button></div>
        </div>
      ) : null}
    </div>
  );
}
/** ---------------- Multi-select dropdown ---------------- */
export function MultiSelectDropdown({
  label,
  values = [],
  onChange,
  options = [],
  placeholder = "Select…",
  className = "w-72",
  disabled = false,
  ariaLabel,
  action,
  menuClassName = "min-w-72",
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const selected = useMemo(() => new Set(values || []), [values]);
  const selectedOptions = options.filter((option) => selected.has(option.value));
  const summary =
    selectedOptions.length === 0
      ? placeholder
      : selectedOptions.length <= 2
      ? selectedOptions.map((option) => option.label).join(", ")
      : `${selectedOptions.length} selected`;

  useEffect(() => {
    const close = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const toggle = (value) => {
    const next = new Set(selected);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onChange?.(Array.from(next));
  };

  return (
    <div ref={rootRef} className={`relative flex flex-col ${className}`}>
      {label ? <span className="mb-1 text-xs font-medium text-muted">{label}</span> : null}
      <button
        type="button"
        aria-label={ariaLabel || label}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
        className={`flex h-8 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 text-left text-sm text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-ring ${
          disabled ? "cursor-not-allowed opacity-70" : ""
        }`}
      >
        <span className={`truncate ${selectedOptions.length ? "" : "text-muted"}`} title={summary}>
          {summary}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-muted" />
      </button>

      {open && !disabled ? (
        <div className={`absolute left-0 top-full z-50 mt-1 max-h-72 w-full overflow-auto rounded-md border border-border bg-app py-1 shadow-lg ${menuClassName}`}>
          {options.length ? (
            options.map((option) => {
              const checked = selected.has(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  role="option"
                  aria-selected={checked}
                  disabled={option.disabled}
                  onClick={() => toggle(option.value)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-app hover:bg-[hsl(var(--muted))/0.25] disabled:opacity-50"
                >
                  <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded border border-input">
                    {checked ? <Check className="h-3 w-3" /> : null}
                  </span>
                  <span className="truncate">{option.label}</span>
                </button>
              );
            })
          ) : (
            <div className="px-3 py-2 text-sm text-muted">No options available</div>
          )}
          {action ? (
            <button
              type="button"
              onClick={async () => {
                await action.onClick?.();
                setOpen(false);
              }}
              className="mt-1 w-full border-t border-border px-3 py-2 text-left text-sm font-medium text-app hover:bg-[hsl(var(--muted))/0.25]"
            >
              {action.label}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** ---------------- Date Input ---------------- */
export function DateInput({
  label,
  value,
  onChange,
  onClear,
  allowedDates = null,
  minDate = "",
  maxDate = "",
  className = "w-full",
  ariaLabel,
  disabled = false,
  name,
  required = false,
  autoComplete,
  ...rest
}) {
  const [tempValue, setTempValue] = useState(value ?? "");
  const inputRef = useRef(null);

  useEffect(() => {
    if (document.activeElement === inputRef.current) return;
    setTempValue(value ?? "");
  }, [value]);

  const isValidISODate = (s) => /^\d{4}-\d{2}-\d{2}$/.test(String(s || ""));

  const commitValue = (val = tempValue) => {
    const iso = val || "";
    const withinAllowed = !allowedDates || allowedDates.includes(iso);
    const withinMinMax =
      (!minDate || iso >= minDate) && (!maxDate || iso <= maxDate);

    if (!iso) {
      if (onClear) {
        const replacement = onClear();
        if (replacement != null) {
          setTempValue(replacement);
          return;
        }
      }
      onChange?.("", null);
      return;
    }

    if (!isValidISODate(iso)) return;

    if (withinAllowed && withinMinMax) {
      onChange?.(iso, null);
    } else {
      setTempValue(value ?? "");
    }
  };

  const min = minDate || (allowedDates?.at(-1) ?? "");
  const max = maxDate || (allowedDates?.[0] ?? "");

  return (
    <label className="flex flex-col">
      {label && (
        <span className="text-xs font-medium text-muted mb-1">{label}</span>
      )}

      <input
        ref={inputRef}
        type="date"
        aria-label={ariaLabel || label}
        name={name}
        required={required}
        autoComplete={autoComplete}
        className={`h-8 text-sm rounded-md border border-input bg-background text-foreground shadow-sm
                    focus:outline-none focus:ring-2 focus:ring-ring px-3 ${className}
                    ${disabled ? "opacity-70 cursor-not-allowed" : ""}`}
        value={tempValue ?? ""}
        onChange={(e) => {
          if (disabled) return;
          const next = e.target.value;
          setTempValue(next);
          if (isValidISODate(next)) commitValue(next);
        }}
        onBlur={() => {
          if (disabled) return;
          commitValue();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commitValue();
            inputRef.current?.blur();
          }
        }}
        disabled={disabled}
        min={min}
        max={max}
        {...rest}
      />
    </label>
  );
}

/** ---------------- Text Input ---------------- */
export function TextInput({
  label,
  value,
  onChange,
  placeholder,
  className = "w-72",
  disabled = false,
  type = "text",
  name,
  required = false,
  autoComplete,
  ariaLabel,
  inputMode,
  pattern,
  readOnly = false,
  ...rest
}) {
  return (
    <label className="flex flex-col">
      {label && (
        <span className="text-xs font-medium text-muted mb-1">{label}</span>
      )}

      <input
        type={type}
        name={name}
        required={required}
        autoComplete={autoComplete}
        aria-label={ariaLabel || label}
        inputMode={inputMode}
        pattern={pattern}
        readOnly={readOnly}
        className={`h-8 text-sm rounded-md border border-input bg-background text-foreground shadow-sm
                    focus:outline-none focus:ring-2 focus:ring-ring px-3 ${className}
                    ${disabled ? "opacity-70 cursor-not-allowed" : ""}`}
        value={value ?? ""}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => {
          if (disabled || readOnly) return;
          onChange?.(e.target.value, e);
        }}
        {...rest}
      />
    </label>
  );
}

/** ---------------- Text Area Input ---------------- */
export function TextAreaInput({
  label,
  value,
  onChange,
  placeholder,
  className = "w-full",
  rows = 4,
  disabled = false,
  name,
  required = false,
  autoComplete,
  ariaLabel,
  readOnly = false,
  ...rest
}) {
  return (
    <label className="flex flex-col">
      {label && (
        <span className="text-xs font-medium text-muted mb-1">{label}</span>
      )}

      <textarea
        name={name}
        required={required}
        autoComplete={autoComplete}
        aria-label={ariaLabel || label}
        readOnly={readOnly}
        rows={rows}
        className={`text-sm rounded-md border border-input bg-background text-foreground shadow-sm
                    focus:outline-none focus:ring-2 focus:ring-ring px-3 py-2 ${className}
                    ${disabled ? "opacity-70 cursor-not-allowed" : ""}`}
        value={value ?? ""}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(e) => {
          if (disabled || readOnly) return;
          onChange?.(e.target.value, e);
        }}
        {...rest}
      />
    </label>
  );
}

/** ---------------- Number Input ---------------- */
export function NumberInput({
  label,
  value,
  onChange,
  type = "decimal",
  precision,
  className = "w-50",
  disabled = false,
  name,
  required = false,
  autoComplete,
  ariaLabel,
  readOnly = false,
  placeholder,
  ...rest
}) {
  const [display, setDisplay] = useState(value ?? "");
  const inputRef = useRef(null);

  const resolvedPrecision =
    typeof precision === "number"
      ? precision
      : type === "percent"
      ? 3
      : type === "currency"
      ? 2
      : type === "decimal"
      ? 2
      : 0;

  const fmtCurrency = (n) =>
    (Number.isFinite(n) ? n : 0).toLocaleString(undefined, {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: resolvedPrecision,
      maximumFractionDigits: resolvedPrecision,
    });

  const fmtPercent = (n) =>
    Number.isFinite(n) ? `${n.toFixed(resolvedPrecision)}%` : "";

  const fmtInteger = (n) =>
    Number.isFinite(n)
      ? Math.trunc(n).toLocaleString(undefined, { maximumFractionDigits: 0 })
      : "";

  const fmtDecimal = (n) =>
    Number.isFinite(n)
      ? n.toLocaleString(undefined, {
          minimumFractionDigits: resolvedPrecision,
          maximumFractionDigits: resolvedPrecision,
        })
      : "";

  const parsePercent = (s) => {
    if (typeof s !== "string") return null;
    const cleaned = s.replace("%", "").trim();
    if (cleaned === "") return null;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  };

  useEffect(() => {
    if (document.activeElement === inputRef.current) return;

    if (value === "" || value == null) {
      setDisplay("");
      return;
    }

    if (type === "currency" && Number.isFinite(value)) {
      setDisplay(fmtCurrency(value));
    } else if (type === "percent" && Number.isFinite(value)) {
      setDisplay(fmtPercent(value));
    } else if (type === "integer" && Number.isFinite(value)) {
      setDisplay(fmtInteger(value));
    } else if (type === "decimal" && Number.isFinite(value)) {
      setDisplay(fmtDecimal(value));
    } else {
      setDisplay(String(value ?? ""));
    }
  }, [value, type, resolvedPrecision]);

  const handleChange = (e) => {
    if (disabled || readOnly) return;

    const rawText = e.target.value;
    setDisplay(rawText);

    if (typeof onChange !== "function") return;

    if (type === "integer") {
      const cleaned = rawText.replace(/[^0-9-]/g, "");
      if (cleaned === "") {
        onChange("", e);
        return;
      }
      const n = Number(cleaned);
      onChange(Number.isFinite(n) ? Math.trunc(n) : "", e);
      return;
    }

    if (type === "percent") {
      onChange(parsePercent(rawText) ?? "", e);
      return;
    }

    const cleaned = rawText.replace(/[^0-9.-]/g, "");
    if (cleaned === "") {
      onChange("", e);
      return;
    }
    const n = Number(cleaned);
    onChange(Number.isFinite(n) ? n : "", e);
  };

  const commitValue = () => {
    const safeDisplay = String(display ?? "");

    if (type === "percent") {
      const n = parsePercent(safeDisplay);
      setDisplay(n == null ? "" : fmtPercent(n));
      return;
    }

    if (type === "currency") {
      const cleaned = safeDisplay.replace(/[^0-9.-]/g, "");
      if (cleaned === "") {
        setDisplay("");
        return;
      }
      const n = Number(cleaned);
      setDisplay(Number.isFinite(n) ? fmtCurrency(n) : "");
      return;
    }

    if (type === "integer") {
      const cleaned = safeDisplay.replace(/[^0-9-]/g, "");
      if (cleaned === "") {
        setDisplay("");
        return;
      }
      const n = Number(cleaned);
      setDisplay(Number.isFinite(n) ? fmtInteger(n) : "");
      return;
    }

    const cleaned = safeDisplay.replace(/[^0-9.-]/g, "");
    if (cleaned === "") {
      setDisplay("");
      return;
    }
    const n = Number(cleaned);
    setDisplay(Number.isFinite(n) ? fmtDecimal(n) : "");
  };

  return (
    <label className="flex flex-col">
      {label && (
        <span className="text-xs font-medium text-muted mb-1">{label}</span>
      )}

      <input
        ref={inputRef}
        type="text"
        name={name}
        required={required}
        autoComplete={autoComplete}
        aria-label={ariaLabel || label}
        inputMode={type === "integer" ? "numeric" : "decimal"}
        pattern={type === "integer" ? "[0-9]*" : undefined}
        readOnly={readOnly}
        className={`h-8 text-sm rounded-md border border-input bg-background text-foreground shadow-sm
                    focus:outline-none focus:ring-2 focus:ring-ring px-3 text-right ${className}
                    ${disabled ? "opacity-70 cursor-not-allowed" : ""}`}
        value={display}
        placeholder={placeholder}
        onChange={handleChange}
        onBlur={() => {
          if (disabled || readOnly) return;
          commitValue();
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commitValue();
            inputRef.current?.blur();
          }
        }}
        disabled={disabled}
        {...rest}
      />
    </label>
  );
}
